import type { ProductCardPayload, ShowProductsMessage } from '@coldpress-voice/shared-types';
import { useEffect, useRef, useState } from 'react';
import { BRAND } from '../lib/brand';
import type { ContactFieldName } from '../lib/contactFields';
import {
  type OrderLine,
  buildOrderLines,
  buildReviewMirror,
  orderReviewSeedSkus,
} from '../lib/orderReview';
import { useDemoStore } from '../store';
import { AttractScene } from './AttractScene';
import { CategoryGrid } from './CategoryGrid';
import { CloseScene } from './CloseScene';
import { EndScene } from './EndScene';
import { HandoffCard } from './HandoffCard';
import { OrderReview } from './OrderReview';
import { ShowcasePanel } from './ShowcasePanel';
import { WelcomeCard } from './WelcomeCard';

/**
 * R13 U2 — how long to hold the "Finding…" tile on a category grid tap before giving up and
 * dropping to the browse panel (the category is exhausted / no cards followed). Matches the
 * ShowcasePanel's own category-load budget: comfortably past the server round-trip + WS latency.
 */
const CATEGORY_LOAD_TIMEOUT_MS = 6_000;

/**
 * Showcase scene container (R10 — showcase-conversation-synergy.md §3; R12 field-fix — close scene,
 * category grid, welcome live-fill, stomp-proofing). This component puts the active scene on stage:
 *   - 'welcome'      → S1 WelcomeCard (contact capture + card scan-to-fill; cold-start mask)
 *   - 'browse'       → S2 ShowcasePanel (categories + product grid + deixis), OR the CategoryGrid
 *                      when the server pushed a category-grid render (F2).
 *   - 'order_review' → S6 OrderReview (confirm before place_order / send_spec_sheet)
 *   - 'close'        → S7 CloseScene (order recap + keyboard-first email + sent/queued state) — R12
 *
 * Scene is driven by BOTH the server (`ui.scene`) and local touch — the store's scene reducer
 * (lib/scene.ts) reconciles the two, keeps the S1 "just browsing" skip sticky, AND keeps 'close'
 * sticky against a post-commit browse (F3). This component is presentation only; transport lives in
 * useVoice (passed down as callbacks).
 *
 * R12 STOMP-PROOF (F1/F3 — "spec-sheet screen flashed then reverted"): while in 'close', this
 * container renders ONLY the close card. A late `ui.show_products` (which updates `cards`) does NOT
 * swap the recap for a product grid — the render is gated by scene, not by the presence of cards.
 * (The scene reducer independently blocks a late server `ui.scene {browse}`.)
 *
 * R11 (§6 #5, S8): the container also hosts the floor-handoff card as a PERSISTENT banner ABOVE the
 * active scene (not a scene swap).
 */

type Props = {
  cards?: ShowProductsMessage;
  selectedSkus: string[];
  onSelectionChange: (skus: string[]) => void;
  connected: boolean;
  conversationId?: string;
  // Browse (ShowcasePanel) transport
  onBrowseCategory?: (categoryId: string) => void;
  onTapToSelect?: (sku: string, name: string) => void;
  onTapToDeselect?: (sku: string, name: string) => void;
  onGridLayout?: (items: Array<{ sku: string; ordinal: number }>) => void;
  onShortlistUpdate?: (skus: string[]) => void;
  // Welcome (S1) transport
  onContactSubmitted?: (
    fields: { name?: string; company?: string },
    method: 'card' | 'voice' | 'typed'
  ) => void;
  // Order review (S6) transport
  onOrderConfirmed?: (items: Array<{ sku: string; qty: number }>) => void;
  // R16 V1 — the live review mirror (contract §14). Emitted on S6 seed, every stepper/removal, and
  // a final emit before any confirm (tap OR voice), so the server mirror never lags the screen.
  onReviewUpdated?: (items: Array<{ sku: string; qty: number }>) => void;
  onCloseEmail?: (fields: {
    email?: string;
    name?: string;
    company?: string;
    country?: string;
    phone?: string;
  }) => void;
  // Close card (S7) Done → server closing beat + S9 end scene (U1).
  onCloseDone?: () => void;
  // R14 B1 — end-scene style-feedback Confirm → `style-feedback {kept, removed, added}`.
  onStyleFeedback?: (feedback: { kept: string[]; removed: string[]; added: string[] }) => void;
  // R17 Q3 (R34 §16) — end-scene chip-state on every toggle + the initial set → `style-chips-updated
  // {selected}` (debounced in EndScene), so Gigi can discuss the chips accurately pre-confirm.
  onStyleChipsUpdated?: (selected: string[]) => void;
  // End scene (S9) "start a new conversation" → full session reset (the only way off 'end').
  onNewConversation?: () => void;
  // R25 — card-scan surface open/close (S1 welcome + S7 close cards) → host mutes the mic + emits the
  // §38 `card-modal` signal for the duration of the scan.
  onScanSurfaceChange?: (active: boolean) => void;
  // R30 V1 — the S7 close-card typing emit (§40.4b `composer-draft`), debounced in VoiceDemo. `field`
  // is 'email'|'phone'; the host coalesces keystrokes and emits the settled draft.
  onDraftChange?: (field: 'email' | 'phone', value: string) => void;
  onDraftStop?: () => void;
};

export function ShowcaseScene({
  cards,
  selectedSkus,
  onSelectionChange,
  // `connected` is still accepted (VoiceDemo passes it) but no longer consumed here since R21 X2
  // removed the legacy browse-panel category grid that used it. Kept on Props for call-site stability
  // and future scenes; intentionally not destructured to avoid an unused-binding lint.
  conversationId,
  onBrowseCategory,
  onTapToSelect,
  onTapToDeselect,
  onGridLayout,
  onShortlistUpdate,
  onContactSubmitted,
  onOrderConfirmed,
  onReviewUpdated,
  onCloseEmail,
  onCloseDone,
  onStyleFeedback,
  onStyleChipsUpdated,
  onNewConversation,
  onScanSurfaceChange,
  onDraftChange,
  onDraftStop,
}: Props) {
  const scene = useDemoStore((s) => s.scene);
  // R30 V2 — the S7 close-field guard setter (holds the close card against a server scene swap while
  // the visitor is mid-typing). CloseScene drives it via onFieldGuardChange → this store flag →
  // applyServerScene refuses to leave 'close'.
  const setCloseFieldGuard = useDemoStore((s) => s.setCloseFieldGuard);
  const contactFields = useDemoStore((s) => s.contactFields);
  const endSnapshot = useDemoStore((s) => s.endSnapshot);
  const endRawPayload = useDemoStore((s) => s.endRawPayload);
  // R14 B2 — voice-confirmed order commit (ui.order_committed) advances S6 → S7 like a Confirm tap.
  const orderCommitted = useDemoStore((s) => s.orderCommitted);
  const setOrderCommitted = useDemoStore((s) => s.setOrderCommitted);
  // R14 B3 — spec-sheet send state (incl. no_order) for the close card's truthful sent-state.
  const specSheetSendState = useDemoStore((s) => s.specSheetSendState);
  const highlightSkus = useDemoStore((s) => s.highlightSkus);
  const setScene = useDemoStore((s) => s.setScene);
  const setTypedContactField = useDemoStore((s) => s.setTypedContactField);
  const applyCardScanFields = useDemoStore((s) => s.applyCardScanFields);
  const setOrderConfirmed = useDemoStore((s) => s.setOrderConfirmed);
  const closeEmailAck = useDemoStore((s) => s.closeEmailAck);
  // R12 — category grid (F2) + welcome-captured auto-advance (F4).
  const categoryGrid = useDemoStore((s) => s.categoryGrid);
  const setCategoryGrid = useDemoStore((s) => s.setCategoryGrid);
  // R17 Q2 — the buffered last-categories payload + its re-applier, for the payload-before-scene
  // race (categories arrived while on welcome/attract; re-show them when browse mounts).
  const bufferedCategories = useDemoStore((s) => s.bufferedCategories);
  const applyBufferedCategories = useDemoStore((s) => s.applyBufferedCategories);
  const welcomeCaptured = useDemoStore((s) => s.welcomeCaptured);
  const setWelcomeCaptured = useDemoStore((s) => s.setWelcomeCaptured);
  // R13 U4 — server-provided per-item qty defaults for the S6 stepper seed (voice orders).
  const orderReviewQty = useDemoStore((s) => s.orderReviewQty);
  // R18 X1 — server-resolved per-item name/image (contract §14 W3); the FIRST source for an order
  // line's label/thumbnail (card cache second, raw SKU last). Fixes the dev-80 "Planters piece" rows.
  const orderReviewItems = useDemoStore((s) => s.orderReviewItems);
  // R19 — the server-acked selection tray (sku → name). A belt-and-braces floor for the S6 seed: if
  // the optimistic `selectedSkus` tray was cleared/raced, the acked SKUs still seed the review card
  // so an `order_review` scene NEVER shows dead plain cards after an order intent (dev-81).
  const ackedSelections = useDemoStore((s) => s.ackedSelections);
  // R11 #5 — floor-handoff banner (persistent above the active scene until dismissed / reset).
  const handoff = useDemoStore((s) => s.handoff);
  const setHandoff = useDemoStore((s) => s.setHandoff);

  // Order lines local to the review/close scenes. Seeded from the current selection + last-shown
  // cards when the review scene opens; re-seeded if the selection changes while off the scene.
  const [orderLines, setOrderLines] = useState<OrderLine[]>([]);

  // R16 V1 — the live review mirror emitter (contract §14). Stepper/removal changes are debounced
  // (~150ms) so a rapid tap-flurry sends one settled mirror; seed + pre-confirm emits flush
  // immediately. Kept in a ref so the debounce timer survives re-renders; `onReviewUpdated` is read
  // through a ref so it isn't a dependency of every emit.
  const mirrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onReviewUpdatedRef = useRef(onReviewUpdated);
  onReviewUpdatedRef.current = onReviewUpdated;
  function emitMirror(lines: OrderLine[], flush = false) {
    if (mirrorTimerRef.current !== null) {
      clearTimeout(mirrorTimerRef.current);
      mirrorTimerRef.current = null;
    }
    const send = () => onReviewUpdatedRef.current?.(buildReviewMirror(lines).items);
    if (flush) {
      send();
      return;
    }
    mirrorTimerRef.current = setTimeout(() => {
      mirrorTimerRef.current = null;
      send();
    }, 150);
  }
  // Flush/cancel any pending debounced mirror on unmount.
  useEffect(() => {
    return () => {
      if (mirrorTimerRef.current !== null) clearTimeout(mirrorTimerRef.current);
    };
  }, []);

  // R13 U2 — category-tap flicker fix. On a category tile tap we DON'T clear the grid immediately
  // (that dropped to the browse panel's own built-in category tiles for a beat before the products
  // landed — tap → categories-again → products). Instead we mark the tapped tile "Finding…" and
  // KEEP the grid mounted until the product payload arrives (an effect below clears it when `cards`
  // updates), so the transition is tap → (tile loading beat) → products.
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Watch for the product payload after a category tap: when new cards arrive, drop the grid so the
  // products take the stage. Keyed on the display_id so a fresh render (not a re-render) clears it.
  const cardsDisplayId = cards?.display_id;
  const hasCards = (cards?.cards.length ?? 0) > 0;
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear the grid only when a NEW card payload lands.
  useEffect(() => {
    if (loadingSlug && hasCards) {
      if (loadingTimerRef.current !== null) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingSlug(null);
      setCategoryGrid(null);
    }
  }, [cardsDisplayId, hasCards]);
  // Cancel the exhaustion timer on unmount (the grid may leave for a scene change mid-load).
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current !== null) clearTimeout(loadingTimerRef.current);
    };
  }, []);

  // R17 Q2 — categories-race recovery. If the server pushed a category grid while the showcase was
  // still on welcome/attract (dev-79 turns 8–9 "I don't see any categories"), the live `categoryGrid`
  // may have been cleared / never rendered before the scene reached browse. On reaching browse with
  // a buffered grid, no live grid, and no product cards on stage, re-apply the buffer so the grid
  // actually renders. Idempotent — applyBufferedCategories no-ops when nothing is buffered, and
  // setCards clears the buffer once the visitor picks a category (so this never re-shows categories
  // over a picked category's pieces).
  useEffect(() => {
    if (scene === 'browse' && !categoryGrid && !hasCards && bufferedCategories) {
      applyBufferedCategories();
    }
  }, [scene, categoryGrid, hasCards, bufferedCategories, applyBufferedCategories]);

  const cardsBySku = new Map<string, ProductCardPayload>();
  for (const c of cards?.cards ?? []) cardsBySku.set(c.sku, c);

  // R18 X1 — a stable signature of the server-resolved item meta (sku→name|image), so the seed
  // effect re-runs when a late `ui.order_committed` folds in real names, but not on unrelated
  // renders. Sorted keys → deterministic string.
  const orderItemMetaKey = Object.entries(orderReviewItems)
    .map(([sku, m]) => `${sku}:${m.name ?? ''}:${m.image ?? ''}`)
    .sort()
    .join('|');

  // R19 — the DEFENSIVE S6 seed set. An `order_review` scene must NEVER render dead plain cards / a
  // bare empty state after an order intent (dev-81: "I see product cards, no review UI"). The tapped
  // SKUs are always known client-side, so union: the server-proposed set (orderReviewQty +
  // orderReviewItems keys) → the optimistic selection tray → the server-acked selections. Even if the
  // scene payload was missing/empty/unparseable, S6 seeds from the local tray. Empty only when nothing
  // is genuinely selected anywhere (then the "nothing selected yet" empty state is correct).
  const proposedPayload = {
    items: [
      ...Object.keys(orderReviewQty).map((sku) => ({ sku })),
      ...Object.keys(orderReviewItems).map((sku) => ({ sku })),
    ],
  };
  // R20 (U2) — the RECONNECT floor. On a fresh reload mid-conversation the scene re-emit is bare and
  // the tray + acks are empty, so sources 1–3 of the seed are all empty and S6 would render dead
  // despite a rehydrated order (the re-emitted `ui.show_products` carries the items). We hand the
  // re-emitted showcase cards to the seed ONLY in that reconnect shape — when NOTHING else seeds AND
  // we're on a review/close scene. Guarding it this way keeps the normal browse→review flow untouched:
  // a visitor who browsed 20 pieces but selected 2 still reviews only their 2 (the tray leads, so the
  // fallback is skipped). `[]` when not reconnecting = the fallback is a no-op.
  const primarySeed = orderReviewSeedSkus(
    proposedPayload,
    selectedSkus,
    Object.keys(ackedSelections)
  );
  const reconnectCardFloor =
    primarySeed.length === 0 && (scene === 'order_review' || scene === 'close')
      ? (cards?.cards ?? []).map((c) => c.sku)
      : [];
  const seedSkus = orderReviewSeedSkus(
    proposedPayload,
    selectedSkus,
    Object.keys(ackedSelections),
    reconnectCardFloor
  );

  // Seed order lines whenever we ENTER the review OR close scene (preserve qty edits while on them).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed only on scene entry + seed-set change.
  useEffect(() => {
    if (scene === 'order_review' || scene === 'close') {
      setOrderLines((prev) => {
        const prevQty = new Map(prev.map((l) => [l.sku, l.qty]));
        // Qty precedence (R13 U4): a visitor's stepper edit (prevQty) > the server's voice-order
        // default (orderReviewQty) > 1. So "one sample each" seeds the steppers, then the visitor
        // adjusts freely and their edits survive a re-seed.
        const next = buildOrderLines(
          seedSkus,
          cardsBySku,
          Object.fromEntries(seedSkus.map((s) => [s, prevQty.get(s) ?? orderReviewQty[s] ?? 1])),
          // R18 X1 — server-resolved name/image win over the card cache so a voice-driven order
          // whose cards scrolled off still shows real names (the R17 SKU fallback only when absent).
          orderReviewItems,
          // R60 F1 — the chip-known names (tap-time, never rotated) as the last name source before
          // the raw SKU: the card cache holds only the LAST grid, so a pick from an earlier grid used
          // to render "Selected piece" on S6 while its chip showed the real name (dev-120 hi).
          ackedSelections
        );
        // R16 V1 — emit the SEED mirror (contract §14) on order-review entry/re-seed so the server's
        // per-session mirror is grounded in the exact screen from the first frame (flushed, no
        // debounce). The close scene is a read-only recap — no mirror there.
        if (scene === 'order_review') emitMirror(next, true);
        return next;
      });
    }
    // R18 X1 — also re-seed when the server-resolved item meta changes (a late `ui.order_committed`
    // carrying names arrives after the scene entered), so the S6/S7 rows upgrade "Planters piece" to
    // the real names in place. Keyed on the meta signature so unrelated renders don't re-seed.
  }, [scene, seedSkus.join(','), orderItemMetaKey]);

  // F4 — welcome auto-advance. Local fallback: if both fields are FINAL and the server hasn't yet
  // driven us off welcome, advance to browse ~2s after the second field finalises. The captured
  // state (brass check + a beat of acknowledgment) shows for that beat first. Reduced-motion users
  // still get the advance (the beat is a timer, not an animation).
  const nameFinal = contactFields.name?.status === 'final';
  const companyFinal = contactFields.company?.status === 'final';
  const bothFinal = nameFinal && companyFinal;
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run on scene + finalisation changes.
  useEffect(() => {
    // Clear any pending timer if we've left welcome or the fields are no longer both final.
    if (scene !== 'welcome' || !bothFinal) {
      if (advanceTimerRef.current !== null) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
      return;
    }
    // Both final + still on welcome: mark captured (shows the confirmed treatment) and arm the
    // local fallback advance. A server scene:'browse' (setWelcomeCaptured + setScene) pre-empts it.
    setWelcomeCaptured(true);
    if (advanceTimerRef.current === null) {
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        if (useDemoStore.getState().scene === 'welcome') setScene('browse');
      }, 2000);
    }
    return () => {
      if (advanceTimerRef.current !== null) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [scene, bothFinal]);

  // R14 B2 — voice-confirm parity. When the server acks a voice-confirmed order commit
  // (`ui.order_committed` → orderCommitted), advance the review scene to the close card exactly as
  // if Confirm had been tapped, then reset the one-shot flag. Guarded to the review scene so a
  // late/duplicate ack can't yank the visitor off a later scene (close/end are terminal-ish).
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire on the orderCommitted one-shot only; emitMirror + orderLines are read at commit time, not a trigger.
  useEffect(() => {
    if (!orderCommitted) return;
    if (scene === 'order_review' || scene === 'browse') {
      // R16 V1 — flush a final mirror before the voice-confirm advance so the server's per-session
      // mirror reflects the exact screen at commit time (dev-77 M1: voice-confirm diverged from the
      // screen). The server treats the mirror as the source of truth on any tap/voice discrepancy.
      emitMirror(orderLines, true);
      setOrderConfirmed(true);
      setScene('close');
    }
    setOrderCommitted(false);
  }, [orderCommitted, scene, setOrderConfirmed, setScene, setOrderCommitted]);

  let sceneEl: React.ReactNode;
  if (scene === 'attract') {
    // S0 attract/welcome resting scene (R15 T1): the pre-connect resting state. The contact card is
    // no longer shown until we connect (scene → 'welcome' via connectScene). No form / category grid
    // / browse pre-connect (T2); session-less browse returns with the R1 degraded-mode build.
    sceneEl = <AttractScene conversationId={conversationId} />;
  } else if (scene === 'welcome') {
    sceneEl = (
      <WelcomeCard
        fields={contactFields}
        captured={welcomeCaptured}
        conversationId={conversationId}
        greeting={typeof BRAND.welcome_greeting === 'string' ? BRAND.welcome_greeting : undefined}
        onType={setTypedContactField}
        onCardScan={(f) => applyCardScanFields(f as Partial<Record<ContactFieldName, string>>)}
        onContinue={(f, method) => {
          onContactSubmitted?.(f, method);
          setWelcomeCaptured(true);
          setScene('browse');
        }}
        onSkip={() => setScene('browse')}
        onScanSurfaceChange={onScanSurfaceChange}
      />
    );
  } else if (scene === 'order_review') {
    sceneEl = (
      <OrderReview
        lines={orderLines}
        onLinesChange={setOrderLines}
        // R16 V1 — every stepper/removal mirrors to the server (debounced) via the container emitter.
        onReviewUpdated={(next) => emitMirror(next)}
        onConfirm={(items) => {
          // R16 V1 — a FINAL flushed mirror precedes order-confirmed so the server commits from the
          // exact screen even if a trailing change's mirror was still debounced.
          emitMirror(orderLines, true);
          onOrderConfirmed?.(items);
          setOrderConfirmed(true);
          // Advance to the S7 close card locally too — the server also emits ui.scene{close},
          // but a local advance means the recap never flashes even if the server signal is slow.
          setScene('close');
        }}
        onBack={() => setScene('browse')}
      />
    );
  } else if (scene === 'close') {
    // S7 close card (R12). STOMP-PROOF: rendered purely by scene — a late ui.show_products cannot
    // pull the product grid back over this.
    sceneEl = (
      <CloseScene
        lines={orderLines}
        fields={contactFields}
        ack={closeEmailAck}
        sendState={specSheetSendState}
        conversationId={conversationId}
        onSubmitEmail={(f) => onCloseEmail?.(f)}
        onType={setTypedContactField}
        onCardScan={(f) =>
          applyCardScanFields({ name: f.name, company: f.company, phone: f.phone })
        }
        onBackToReview={() => setScene('order_review')}
        onDone={() => {
          // R13 U1: Done is the deliberate goodbye — tell the server (closing beat + ui.scene:end)
          // AND advance to the terminal S9 end scene locally so it never reverts to browse (#3b).
          // R30 V2 — Done is a LOCAL touch (full peer), so it must NOT be blocked by the field guard.
          // Release the guard first so the local `setScene('end')` (applyLocalScene, which the guard
          // never gates) lands cleanly, and no stale guard traps the next visitor.
          setCloseFieldGuard(false);
          onCloseDone?.();
          setScene('end');
        }}
        onScanSurfaceChange={onScanSurfaceChange}
        // R30 V1 — the typing emit (§40.4b) from the S7 email/phone fields; debounced in VoiceDemo.
        onDraftChange={onDraftChange}
        onDraftStop={onDraftStop}
        // R30 V2 — the focus-guard: hold the close card against a server scene swap while a field is
        // focused with a non-empty draft (dev-117). Client-local store flag; independent of the server.
        onFieldGuardChange={setCloseFieldGuard}
      />
    );
  } else if (scene === 'end') {
    // S9 terminal end scene (R13 U1): style-snapshot reveal + thanks + feedback. Sticky — the scene
    // reducer blocks any move off 'end'; the ONLY way out is the full session reset below.
    sceneEl = (
      <EndScene
        snapshot={endSnapshot ?? { styles: [], finishes: [], materials: [], colourTones: [] }}
        rawPayload={endRawPayload}
        onStyleFeedback={(feedback) => onStyleFeedback?.(feedback)}
        onStyleChipsUpdated={(selected) => onStyleChipsUpdated?.(selected)}
        onNewConversation={() => onNewConversation?.()}
        conversationId={conversationId}
        // R22 K3 — the R21 (X1) email-capture-over-end fallback is RETIRED (USER-SPECIFIED). An
        // out-of-flow email/phone ask now routes to the Type composer (§29 / K2 pulse), not a card
        // mounted over the goodbye — so EndScene no longer takes the close-email path or ack props.
      />
    );
  } else if (categoryGrid) {
    // Browse scene, category-grid mode (F2): the server pushed the category list — SHOW it.
    sceneEl = (
      <CategoryGrid
        items={categoryGrid}
        loadingSlug={loadingSlug}
        onSelect={(slug) => {
          onBrowseCategory?.(slug);
          // R13 U2 — flicker fix: mark the tapped tile "Finding…" and KEEP the grid mounted until
          // the product payload arrives (the effect above clears it on new cards), so we never flash
          // the browse panel's own category tiles in between. If the category is exhausted (no cards
          // within the window), drop to the browse panel — Gigi's spoken line covers the experience.
          setLoadingSlug(slug);
          if (loadingTimerRef.current !== null) clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = setTimeout(() => {
            loadingTimerRef.current = null;
            setLoadingSlug(null);
            setCategoryGrid(null);
          }, CATEGORY_LOAD_TIMEOUT_MS);
        }}
      />
    );
  } else {
    // Default: browse. Product cards when present, else the R21 X2 quiet waiting state. The category
    // TILES are NOT here anymore — the server's `ui.categories` grid (CategoryGrid, the `categoryGrid`
    // branch above) is the single source of browse tiles. `onBrowseCategory`/`connected` are no longer
    // passed here: they served the removed legacy 8-tile grid (the dev-86 stray-screen bug).
    sceneEl = (
      <ShowcasePanel
        cards={cards}
        selectedSkus={selectedSkus}
        onSelectionChange={onSelectionChange}
        onTapToSelect={onTapToSelect}
        onTapToDeselect={onTapToDeselect}
        onGridLayout={onGridLayout}
        highlightSkus={highlightSkus}
        onShortlistUpdate={onShortlistUpdate}
      />
    );
  }

  // R11 #5: the floor-handoff banner is pinned above whichever scene is on stage.
  if (!handoff) return <>{sceneEl}</>;
  return (
    <div className="flex flex-col gap-5">
      <HandoffCard handoff={handoff} onDismiss={() => setHandoff(null)} />
      {sceneEl}
    </div>
  );
}
